// Curriculum rail — what CSOC students see in place of the Daily Leaderboard
// (master-class students excepted — see Dashboard/index.tsx, they get the
// leaderboard too since there's no batch curriculum to show).
//
// Data comes from dashboard/v1/summary's `csoc_setup_dashboard.webinar_list`
// / `.past_webinar_list`, which the backend builds from the student's real
// scheduled webinars (dashboard/csoc_setup.py). Cancelled sessions are
// already dropped there, but `status` (see stateOf below) is kept for the
// completed-vs-pending split that's still needed.

import React, {useRef, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome5';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import Reveal from '@/Atoms/Reveal';
import AnimatedBar from '@/Atoms/AnimatedBar';
import SkeletonBar from '@/Atoms/SkeletonBar';
import ScrollHint from '../LeaderboardPreview/ScrollHint';
import {Webinar} from '../../personaHelpers';
import {radii} from '@/Utils/radius';

const BORDER = 'rgba(38,35,34,0.08)';
const TEAL = '#3DAB9E';
const TEAL_TINT = '#E7F4F1';
const INK = '#262322';
const MUTED = '#79736F';

interface CurriculumPanelProps {
  /** past_webinar_list — rendered as completed. */
  past: Webinar[];
  /** webinar_list — rendered as upcoming. */
  upcoming: Webinar[];
  /**
   * True when the dashboard has stacked into one column (mobile/tablet). The
   * rail is height-capped by its parent on desktop, but stacked it has no
   * bound at all — and a student with 160+ sessions would otherwise get a
   * single card taller than the rest of the page put together.
   */
  stacked?: boolean;
}

type SessionState = 'completed' | 'cancelled' | 'pending';

interface Session {
  key: string;
  webinarId?: number;
  number?: number;
  topic: string;
  date: string;
  state: SessionState;
}

const isPsychologySession = (topic: string) => /psychology/i.test(topic || '');

/**
 * The backend (dashboard/csoc_setup.py) already splits each session into
 * exactly one of past_webinar_list/webinar_list by its own resolved
 * `status`, so which list a row came from already implies its state —
 * `stateOf` still reads `status` directly rather than the list split
 * itself, since that's the one place the backend's actual verdict lives.
 */
const stateOf = (w: Webinar, listIsPast: boolean): SessionState => {
  if (w?.status === 'cancelled' || w?.was_cancelled) return 'cancelled';
  if (w?.status === 'completed') return 'completed';
  if (w?.status === 'scheduled' || w?.status === 'updating') return 'pending';
  // No status on this row (older/partial payload) — fall back to the list it
  // came from, the previous behaviour.
  return listIsPast ? 'completed' : 'pending';
};

const toSessions = (past: Webinar[], upcoming: Webinar[]): Session[] => {
  const map = (list: Webinar[], listIsPast: boolean) =>
    (list || [])
      // Psychology sessions don't belong in the curriculum rail at all —
      // drop them here, before dedup/counting, rather than just styling
      // them differently.
      .filter(w => !isPsychologySession(w?.topic || ''))
      .map((w, i) => ({
        key: `${listIsPast ? 'past' : 'next'}-${w?.webinar_id ?? i}`,
        webinarId: w?.webinar_id,
        number: w?.session_number,
        topic: w?.topic || 'Session',
        date: w?.webinar_date || '',
        state: stateOf(w, listIsPast),
      }))
      // Cancelled sessions never happened and aren't coming back — nothing
      // useful to show the student, so drop them entirely rather than
      // rendering a "cancelled" row.
      .filter(s => s.state !== 'cancelled');

  // De-dupe by webinar_id across the two lists as a safety net — the
  // backend already splits each session into exactly one list, but this
  // guards against a webinar_id ever appearing in both. A row with no id
  // is kept as-is (nothing to match it against).
  const seen = new Set<number>();
  const all = [...map(past, true), ...map(upcoming, false)].filter(s => {
    if (s.webinarId == null) return true;
    if (seen.has(s.webinarId)) return false;
    seen.add(s.webinarId);
    return true;
  });

  // Plain chronological order (session_number only breaks ties within a
  // day) — then the single nearest-pending row is pinned to the front
  // below, so the rail reads as "next class, then the rest of the
  // curriculum in date order" rather than every upcoming session bunched
  // above every past one.
  const byDate = all.sort((a, b) => {
    const dateCompare = String(a.date).localeCompare(String(b.date));
    if (dateCompare !== 0) {
      return dateCompare;
    }
    return (a.number ?? 0) - (b.number ?? 0);
  });

  const nextIndex = byDate.findIndex(s => s.state === 'pending');
  if (nextIndex <= 0) {
    return byDate;
  }
  const [next] = byDate.splice(nextIndex, 1);
  byDate.unshift(next);
  return byDate;
};

const formatDate = (value: string) => {
  if (!value) {
    return '';
  }
  // webinar_date is a plain YYYY-MM-DD. Split it rather than `new Date(...)`:
  // that parses a bare date as UTC and can render the previous day east of
  // Greenwich — the same trap the persona date handling hit.
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) {
    return String(value);
  }
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
};

/**
 * Neutral placeholder for the rail while the persona is still resolving.
 *
 * Which panel belongs here depends on an async webinar fetch. Rendering the
 * leaderboard meanwhile meant CSOC students watched it load, settle, and then
 * get replaced by the curriculum a second later. This commits to the card
 * shape — which both panels share — but to none of the copy.
 */
export function RailSkeleton() {
  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerIcon} />
        <View style={{flex: 1, minWidth: 0, gap: 6}}>
          <SkeletonBar width="55%" height={16} />
          <SkeletonBar width="80%" height={12} />
        </View>
      </View>
      <View style={{marginTop: 16, gap: 12}}>
        {[0, 1, 2, 3, 4].map(i => (
          <SkeletonBar key={i} width="100%" height={44} />
        ))}
      </View>
    </View>
  );
}

export default function CurriculumPanel({
  past,
  upcoming,
  stacked,
}: CurriculumPanelProps) {
  const sessions = toSessions(past, upcoming);
  const completed = sessions.filter(s => s.state === 'completed').length;
  // Cancelled sessions are dropped entirely in toSessions, so there's
  // nothing to exclude from this denominator here.
  const total = sessions.length;
  const percent = total ? Math.round((completed / total) * 100) : 0;
  // The first session still to come.
  const nextKey = sessions.find(s => s.state === 'pending')?.key;

  // Sized off the panel's own width: it's a narrow rail beside the hero on
  // desktop but full-bleed when the dashboard stacks on a phone, so the
  // viewport tells us nothing useful here.
  const [width, setWidth] = useState(0);
  const compact = width > 0 && width < 340;

  const scrollRef = useRef<any>(null);
  const [contentH, setContentH] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const overflowing = contentH - viewportH > 8;

  return (
    <View
      style={[styles.card, compact && styles.cardCompact]}
      onLayout={e => setWidth(e.nativeEvent.layout.width)}>
      <View style={styles.header}>
        <View style={styles.headerIcon}>
          <Icon name="graduation-cap" size={17} color={CCColors.Primary.Brown} />
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <CCText fontSize={compact ? 17 : 19} fontWeight="bold" style={styles.title}>
            Your curriculum
          </CCText>
          <CCText fontSize={13} fontWeight="semibold" style={styles.count}>
            {total
              ? `${completed} of ${total} sessions done`
              : 'Your sessions will appear here'}
          </CCText>
        </View>
        {total ? (
          <View style={styles.percentPill}>
            <CCText fontSize={14} fontWeight="bold" style={{color: TEAL}}>
              {percent}%
            </CCText>
          </View>
        ) : null}
      </View>

      {total ? (
        <AnimatedBar
          percent={percent}
          color={TEAL}
          trackColor={TEAL_TINT}
          height={7}
          style={styles.progress}
        />
      ) : null}

      {total ? (
        <View style={{flex: 1, minHeight: 0}}>
          <ScrollView
            ref={scrollRef}
            nestedScrollEnabled
            style={[styles.list, stacked && styles.listStacked]}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            scrollEventThrottle={16}
            onScroll={() => setScrolled(true)}
            onLayout={e => setViewportH(e.nativeEvent.layout.height)}
            onContentSizeChange={(_w, h) => setContentH(h)}>
            {sessions.map((s, i) => {
              const isNext = s.key === nextKey;
              return (
                <Reveal key={s.key} delay={Math.min(i, 8) * 45} distance={10}>
                  <View
                    style={[
                      styles.row,
                      isNext && styles.rowNext,
                      compact && styles.rowCompact,
                    ]}>
                    <View
                      style={[
                        styles.badge,
                        s.state === 'completed' && styles.badgeDone,
                        isNext && styles.badgeNext,
                      ]}>
                      {s.state === 'completed' ? (
                        <Icon
                          name="check"
                          size={13}
                          color={CCColors.White}
                          solid
                        />
                      ) : (
                        <CCText
                          fontSize={13}
                          fontWeight="bold"
                          style={{color: isNext ? CCColors.White : TEAL}}>
                          {s.number ?? i + 1}
                        </CCText>
                      )}
                    </View>

                    <View style={styles.copy}>
                      <View style={styles.kickerRow}>
                        <CCText fontSize={11.5} fontWeight="semibold" style={styles.kicker}>
                          {s.number != null ? `Session ${s.number}` : 'Session'}
                          {s.date ? ` · ${formatDate(s.date)}` : ''}
                        </CCText>
                        {isNext ? (
                          <View style={styles.nextChip}>
                            <CCText fontSize={9.5} fontWeight="bold" color={CCColors.White}>
                              NEXT
                            </CCText>
                          </View>
                        ) : null}
                      </View>
                      <CCText
                        fontSize={14}
                        fontWeight="bold"
                        showEllipses
                        style={[
                          styles.topic,
                          s.state === 'completed' && styles.topicDone,
                        ]}>
                        {s.topic}
                      </CCText>
                    </View>

                    {/* An icon, not the word COMPLETED: at this row height the
                        label crowded the topic and wrapped on a narrow rail. */}
                    {s.state === 'completed' ? (
                      <Icon
                        name="check-circle"
                        size={17}
                        color={CCColors.Green}
                        solid
                        style={styles.stateIcon}
                      />
                    ) : (
                      <Icon
                        name="clock"
                        size={16}
                        color="rgba(38,35,34,0.22)"
                        style={styles.stateIcon}
                      />
                    )}
                  </View>
                </Reveal>
              );
            })}
          </ScrollView>

          {/* Same "more below" affordance as the leaderboard rail: the list is
              height-capped and its scrollbar is hidden, so without this there's
              no signal that the rest of the curriculum exists. */}
          <ScrollHint
            visible={overflowing && !scrolled}
            onPress={() =>
              scrollRef.current?.scrollTo({
                y: Math.max(0, contentH - viewportH),
                animated: true,
              })
            }
          />
        </View>
      ) : (
        <View style={styles.empty}>
          <Icon name="calendar-alt" size={24} color="#8D8884" />
          <CCText fontSize={13} style={styles.emptyText}>
            Your curriculum will appear here once your classes are scheduled.
          </CCText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    minHeight: 0,
    backgroundColor: CCColors.White,
    borderRadius: radii.panel,
    padding: 20,
    // Vertical only: the rail COLUMN carries the 8px horizontal inset, so a
    // horizontal margin here would push this card 8px further in than the
    // sections beside it.
    marginVertical: 8,
    alignSelf: 'stretch',
    flexShrink: 1,
    minWidth: 0,
    shadowColor: CCColors.Primary.Dark,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.05,
    overflow: 'hidden',
  },
  cardCompact: {
    padding: 14,
    borderRadius: radii.panel,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: CCColors.Primary.lightBrown,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {color: INK},
  count: {color: MUTED, marginTop: 2},
  percentPill: {
    backgroundColor: TEAL_TINT,
    borderRadius: 99,
    paddingHorizontal: 10,
    paddingVertical: 5,
    flexShrink: 0,
  },
  progress: {marginTop: 14},
  list: {marginTop: 6},
  listStacked: {maxHeight: 380},
  listContent: {paddingBottom: 26},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 12,
  },
  rowCompact: {
    paddingVertical: 9,
    gap: 10,
  },
  rowNext: {
    backgroundColor: '#F4FBF9',
    borderRadius: radii.chip,
    borderBottomColor: 'transparent',
    paddingHorizontal: 8,
  },
  badge: {
    width: 34,
    height: 34,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 17,
    backgroundColor: TEAL_TINT,
  },
  badgeDone: {backgroundColor: CCColors.Green},
  badgeNext: {backgroundColor: TEAL},
  copy: {flex: 1, minWidth: 0},
  kickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  kicker: {color: MUTED},
  nextChip: {
    backgroundColor: TEAL,
    borderRadius: 5,
    paddingHorizontal: 5,
    paddingVertical: 1,
    flexShrink: 0,
  },
  topic: {color: INK, marginTop: 2},
  topicDone: {color: MUTED},
  stateIcon: {flexShrink: 0},
  empty: {
    minHeight: 170,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  emptyText: {
    color: '#8D8884',
    textAlign: 'center',
    marginTop: 10,
    lineHeight: 19,
  },
});
